import React, { useState } from "react";
import { FiArrowDown, FiArrowUp, FiChevronDown, FiChevronRight, FiPlus, FiTrash2 } from "react-icons/fi";
import { reorderList } from "./utils";
import HrefInput from "../HrefInput";
import type { HrefTarget } from "../../types/layout";

type JsonSchema = {
	title?: string;
	description?: string;
	type?: string | string[];
	properties?: Record<string, JsonSchema>;
	items?: JsonSchema;
	default?: any;
};

type Path = Array<string | number>;

const pickPrimaryType = (type?: string | string[]): string | undefined => {
	if (!type) return undefined;
	if (Array.isArray(type)) {
		const nonNullable = type.find((entry) => entry !== "null" && entry !== "undefined");
		return nonNullable ?? type[0];
	}
	return type;
};

const cloneValue = <T,>(value: T): T => {
	if (typeof structuredClone === "function") {
		// @ts-ignore - structuredClone might not be in the TS lib target
		return structuredClone(value);
	}
	return JSON.parse(JSON.stringify(value));
};

const buildDefaultValue = (schema?: JsonSchema): any => {
	if (!schema) return null;
	if (schema.default !== undefined) return cloneValue(schema.default);
	const primaryType = pickPrimaryType(schema.type);
	switch (primaryType) {
		case "object": {
			const props = schema.properties ?? {};
			const result: Record<string, any> = {};
			for (const key of Object.keys(props)) {
				result[key] = buildDefaultValue(props[key]);
			}
			return result;
		}
		case "array":
			return [];
		case "boolean":
			return false;
		case "number":
		case "integer":
			return 0;
		case "string":
		default:
			return "";
	}
};

const setValueAtPath = (target: any, path: Path, value: any) => {
	if (!path.length) return;
	let cursor = target;
	for (let i = 0; i < path.length - 1; i++) {
		const key = path[i];
		if (cursor[key] === undefined) {
			cursor[key] = typeof path[i + 1] === "number" ? [] : {};
		}
		cursor = cursor[key];
	}
	cursor[path[path.length - 1]] = value;
};

const FieldLabel: React.FC<{ title?: string; description?: string }> = ({ title, description }) => (
	<div className="flex flex-col gap-1">
		{title && <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</label>}
		{description && <p className="text-xs text-slate-500">{description}</p>}
	</div>
);

const DEFAULT_ITEM_SUMMARY_KEYS = ["title", "description", "label", "name", "text", "heading", "tag"];

function getItemSummary(item: any, preferredKeys: string[] = DEFAULT_ITEM_SUMMARY_KEYS): string {
	if (item == null) return "Item";
	if (typeof item === "string") return item.trim().slice(0, 60) + (item.trim().length > 60 ? "…" : "") || "Item";
	const obj = typeof item === "object" ? item : {};
	for (const k of preferredKeys) {
		const v = obj[k];
		if (typeof v === "string" && v.trim()) return v.trim().slice(0, 80) + (v.trim().length > 80 ? "…" : "");
	}
	for (const v of Object.values(obj)) {
		if (typeof v === "string" && (v as string).trim()) return (v as string).trim().slice(0, 80) + ((v as string).trim().length > 80 ? "…" : "");
	}
	return "Item";
}

const CollapsibleSection: React.FC<{
	label: string;
	defaultOpen?: boolean;
	children: React.ReactNode;
	className?: string;
}> = ({ label, defaultOpen = false, children, className = "" }) => {
	const [open, setOpen] = useState(defaultOpen);
	return (
		<div className={`overflow-hidden rounded-xl border border-slate-200 bg-slate-50 ${className}`.trim()}>
			<div
				role="button"
				tabIndex={0}
				onClick={() => setOpen((o) => !o)}
				onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((o) => !o)}
				className="flex items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-100"
				aria-expanded={open}
			>
				{open ? (
					<FiChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
				) : (
					<FiChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
				)}
				<span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</span>
			</div>
			{open && <div className="space-y-3 border-t border-slate-200 bg-white p-3">{children}</div>}
		</div>
	);
};

const TextField: React.FC<{
	title?: string;
	description?: string;
	value: string;
	editable: boolean;
	onChange: (next: string) => void;
	placeholder?: string;
}> = ({ title, description, value, editable, onChange, placeholder }) => (
	<div className="space-y-1">
		<FieldLabel title={title} description={description} />
		<input
			type="text"
			value={value ?? ""}
			onChange={(event) => onChange(event.target.value)}
			placeholder={placeholder}
			className="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
			disabled={!editable}
		/>
	</div>
);

const BooleanField: React.FC<{
	title?: string;
	value: boolean;
	editable: boolean;
	onChange: (next: boolean) => void;
}> = ({ title, value, editable, onChange }) => (
	<label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
		<input
			type="checkbox"
			className="h-4 w-4 rounded border-slate-300 text-slate-900"
			checked={value ?? false}
			disabled={!editable}
			onChange={(event) => onChange(event.target.checked)}
		/>
		{title}
	</label>
);

const ArrayControls: React.FC<{
	index: number;
	length: number;
	editable: boolean;
	onMoveUp: () => void;
	onMoveDown: () => void;
	onDelete: () => void;
}> = ({ index, length, editable, onMoveUp, onMoveDown, onDelete }) => (
	<div className="flex flex-wrap items-center gap-1">
		<button
			type="button"
			onClick={onMoveUp}
			disabled={!editable || index === 0}
			className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
		>
			<FiArrowUp /> Up
		</button>
		<button
			type="button"
			onClick={onMoveDown}
			disabled={!editable || index === length - 1}
			className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
		>
			<FiArrowDown /> Down
		</button>
		<button
			type="button"
			onClick={onDelete}
			disabled={!editable}
			className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-600 transition hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-50"
		>
			<FiTrash2 /> Delete
		</button>
	</div>
);

const ArrayOfStringsField: React.FC<{
	schema: JsonSchema;
	value: string[];
	onChange: (next: string[]) => void;
	editable: boolean;
	title?: string;
}> = ({ schema, value, onChange, editable, title }) => {
	const items = Array.isArray(value) ? value : [];
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	return (
		<div className="space-y-2 rounded-xl border border-slate-100 bg-slate-50 p-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title ?? schema.title ?? "Items"}</p>
				{editable && (
					<button
						type="button"
						onClick={() => {
							onChange([...items, buildDefaultValue(schema.items) ?? ""]);
							setExpandedIndex(items.length);
						}}
						className="inline-flex items-center rounded-full border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 transition hover:border-slate-300 hover:bg-white"
					>
						<FiPlus className="mr-1" /> Add
					</button>
				)}
			</div>
			{items.length === 0 && <p className="text-xs text-slate-500">No entries yet.</p>}
			{items.map((item, index) => {
				const isExpanded = expandedIndex === index;
				const s = (item ?? "").toString().trim();
				const summary = (s.slice(0, 60) + (s.length > 60 ? "…" : "")) || `Item ${index + 1}`;
				return (
					<div key={index} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
						<div
							role="button"
							tabIndex={0}
							onClick={() => setExpandedIndex((i) => (i === index ? null : index))}
							onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setExpandedIndex((i) => (i === index ? null : index))}
							className="flex min-w-0 items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
							aria-expanded={isExpanded}
						>
							{isExpanded ? (
								<FiChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
							) : (
								<FiChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
							)}
							<span className="min-w-0 flex-1 truncate text-sm text-slate-700">{summary || `Item ${index + 1}`}</span>
						</div>
						{isExpanded && (
							<div className="space-y-2 border-t border-slate-100 bg-slate-50/50 p-3">
								<input
									type="text"
									value={item ?? ""}
									onChange={(event) => {
										const next = [...items];
										next[index] = event.target.value;
										onChange(next);
									}}
									className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none disabled:cursor-not-allowed disabled:bg-slate-50"
									disabled={!editable}
								/>
								{editable && (
									<ArrayControls
										index={index}
										length={items.length}
										editable={editable}
										onMoveUp={() => onChange(reorderList(items, index, Math.max(0, index - 1)))}
										onMoveDown={() => onChange(reorderList(items, index, Math.min(items.length - 1, index + 1)))}
										onDelete={() => {
											onChange(items.filter((_, i) => i !== index));
											setExpandedIndex(null);
										}}
									/>
								)}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

const ArrayOfObjectsField: React.FC<{
	schema: JsonSchema;
	value: any[];
	onChange: (next: any[]) => void;
	editable: boolean;
}> = ({ schema, value, onChange, editable }) => {
	const items = Array.isArray(value) ? value : [];
	const summaryKeys = schema.items?.properties ? Object.keys(schema.items.properties).slice(0, 5) : DEFAULT_ITEM_SUMMARY_KEYS;
	const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
	return (
		<div className="space-y-2 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">{schema.title ?? "Items"}</p>
					{schema.description && <p className="text-xs text-slate-500">{schema.description}</p>}
				</div>
				{editable && (
					<button
						type="button"
						onClick={() => {
							onChange([...(items ?? []), buildDefaultValue(schema.items)]);
							setExpandedIndex(items.length);
						}}
						className="inline-flex items-center rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
					>
						<FiPlus className="mr-2" /> Add
					</button>
				)}
			</div>
			{items.length === 0 && <p className="text-sm text-slate-500">No entries yet.</p>}
			{items.map((item, index) => {
				const isExpanded = expandedIndex === index;
				const summary = getItemSummary(item, summaryKeys) || `Item ${index + 1}`;
				return (
					<div key={index} className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
						<div
							role="button"
							tabIndex={0}
							onClick={() => setExpandedIndex((i) => (i === index ? null : index))}
							onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setExpandedIndex((i) => (i === index ? null : index))}
							className="flex min-w-0 items-center gap-2 px-3 py-2 text-left transition hover:bg-slate-50"
							aria-expanded={isExpanded}
						>
							{isExpanded ? (
								<FiChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
							) : (
								<FiChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
							)}
							<span className="min-w-0 flex-1 truncate text-sm text-slate-700">{summary}</span>
						</div>
						{isExpanded && (
							<div className="space-y-3 border-t border-slate-100 bg-slate-50/50 p-4">
								<SchemaObject
									schema={schema.items ?? {}}
									value={item}
									onChange={(nextValue) => {
										const next = [...items];
										next[index] = nextValue;
										onChange(next);
									}}
									editable={editable}
								/>
								{editable && (
									<ArrayControls
										index={index}
										length={items.length}
										editable={editable}
										onMoveUp={() => onChange(reorderList(items, index, Math.max(0, index - 1)))}
										onMoveDown={() => onChange(reorderList(items, index, Math.min(items.length - 1, index + 1)))}
										onDelete={() => {
											onChange(items.filter((_, i) => i !== index));
											setExpandedIndex(null);
										}}
									/>
								)}
							</div>
						)}
					</div>
				);
			})}
		</div>
	);
};

const SchemaField: React.FC<{
	schema: JsonSchema;
	value: any;
	onChange: (next: any) => void;
	editable: boolean;
}> = ({ schema, value, onChange, editable }) => {
	const primaryType = pickPrimaryType(schema.type);
	const properties = schema.properties ?? {};
	const isHrefObject =
		primaryType === "object" &&
		(("slug" in properties && "external" in properties) || ("slug" in properties && "pageId" in properties));

	if (isHrefObject) {
		const hrefValue: HrefTarget | null = value ?? null;
		return (
			<div className="space-y-1">
				<FieldLabel title={schema.title} description={schema.description} />
				<HrefInput
					value={hrefValue}
					onChange={(next) => onChange(next ?? { slug: "" })}
					className="w-full"
					disabled={!editable}
				/>
			</div>
		);
	}

	if (primaryType === "boolean") {
		return <BooleanField title={schema.title} value={value ?? false} editable={editable} onChange={onChange} />;
	}

	if (primaryType === "string" || primaryType === "number" || primaryType === "integer" || !primaryType) {
		return (
			<TextField
				title={schema.title}
				description={schema.description}
				value={value ?? ""}
				editable={editable}
				onChange={onChange}
			/>
		);
	}

	if (primaryType === "array") {
		const itemsType = pickPrimaryType(schema.items?.type);
		if (itemsType === "string" || itemsType === "number" || itemsType === "integer" || !itemsType) {
			return (
				<ArrayOfStringsField
					schema={schema}
					value={Array.isArray(value) ? value : []}
					onChange={onChange}
					editable={editable}
					title={schema.title}
				/>
			);
		}

		if (itemsType === "object") {
			return <ArrayOfObjectsField schema={schema} value={Array.isArray(value) ? value : []} onChange={onChange} editable={editable} />;
		}
	}

	if (primaryType === "object") {
		return <SchemaObject schema={schema} value={value ?? {}} onChange={onChange} editable={editable} />;
	}

	return null;
};

const SchemaObject: React.FC<{
	schema: JsonSchema;
	value: any;
	onChange: (next: any) => void;
	editable: boolean;
}> = ({ schema, value, onChange, editable }) => {
	const properties = schema.properties ?? {};

	return (
		<div className="space-y-3">
			{Object.entries(properties).map(([key, childSchema]) => {
				const childType = pickPrimaryType(childSchema.type);
				const isNestedObject = childType === "object";
				const field = (
					<SchemaField
						schema={childSchema}
						value={value?.[key]}
						onChange={(nextValue) => onChange({ ...(value ?? {}), [key]: nextValue })}
						editable={editable}
					/>
				);
				return (
					<div key={key} className="space-y-2">
						{isNestedObject ? (
							<CollapsibleSection label={childSchema.title ?? key}>
								{field}
							</CollapsibleSection>
						) : (
							field
						)}
					</div>
				);
			})}
		</div>
	);
};

/** Generic JSON schema-driven editor for site layout configuration. */
export const LayoutSchemaEditor: React.FC<{
	title: string;
	description?: string;
	schema: JsonSchema;
	value: any;
	onChange: (path: Path, value: any) => void;
	path: Path;
	editable: boolean;
}> = ({ title, description, schema, value, onChange, path, editable }) => {
	const handleChange = (relativePath: Path, next: any) => {
		onChange([...path, ...relativePath], next);
	};

	return (
		<fieldset disabled={!editable} className="max-w-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<div>
					<p className="text-xs uppercase tracking-[0.3em] text-slate-400">{title}</p>
					{description && <p className="text-sm text-slate-500">{description}</p>}
				</div>
			</div>
			<div className="mt-4 space-y-4">
				{Object.entries(schema.properties ?? {}).map(([key, childSchema]) => (
					<CollapsibleSection key={key} label={childSchema.title ?? key}>
						<SchemaField
							schema={childSchema}
							value={value?.[key]}
							onChange={(nextValue) => {
								const current = value ?? {};
								const cloned = { ...current };
								setValueAtPath(cloned, [key], nextValue);
								handleChange([], cloned);
							}}
							editable={editable}
						/>
					</CollapsibleSection>
				))}
			</div>
		</fieldset>
	);
};

export const setLayoutValue = (draft: any, path: Path, value: any) => {
	setValueAtPath(draft, path, value);
};
